import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Panel, PanelBody, PanelHeader } from '../../components/ui/Panel';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Pill from '../../components/ui/Pill';
import { EmptyState, ErrorNotice, InfoNotice, LoadingPanel, SkeletonRows } from '../../components/ui/States';
import ApplicationView from '../../components/review/ApplicationView';
import {
  CalibrationTable,
  DecisionCell,
  DeliberationTable,
  EquitySummary,
  ExportModal,
} from '../../components/review/CommitteeViews';
import { HeadRow, StatusPill, Td, Th, inputClass } from '../../components/review/parts';
import {
  ApplicationView as View,
  AuditRow,
  Calibration,
  Conflict,
  CycleDetail,
  Deliberation,
  OUTCOME_LABELS,
  Progress,
  StaffApplication,
  downloadCsv,
  errorMessage,
  showValue,
  staffApi,
  downloadAttachment,
  staffBase,
  stateLabel,
} from '../../services/reviewApi';
import { utcStamp } from '../../lib/format';
import { useCycleRefresh } from './useCycleRefresh';
import { promptAction } from '../../components/ui/Confirm';

const path = (cycle: CycleDetail, rest = '') => `${staffBase}/cycles/${cycle.id}${rest}`;

function ApplicationModal({
  cycle,
  applicationId,
  onClose,
}: {
  cycle: CycleDetail;
  applicationId: string;
  onClose: () => void;
}) {
  const view = useQuery<View>(
    ['review', cycle.id, 'application', applicationId],
    async () => (await staffApi.get(path(cycle, `/applications/${applicationId}`))).data.data,
  );
  return (
    <Modal title={view.data?.identity?.name ?? 'Application'} onClose={onClose} width={860}>
      {view.isLoading ? (
        <LoadingPanel label="Loading…" height={160} />
      ) : view.error || !view.data ? (
        <ErrorNotice>{errorMessage(view.error, 'Could not load the application.')}</ErrorNotice>
      ) : (
        <div>
          <ApplicationView
            view={view.data}
            onDownload={(fieldId, label) =>
              downloadAttachment(staffApi, path(cycle, `/applications/${applicationId}/files/${fieldId}`), label)
            }
          />
          {view.data.assignments?.length ? (
            <>
              <h3 className="font-serif font-semibold text-table mt-6 mb-2">Assignments</h3>
              {view.data.assignments.map((a) => (
                <div key={a.id} className="text-small py-1 border-b border-line">
                  {a.reviewer} · <StatusPill status={a.state} /> {a.purpose === 'third' ? '· third review' : ''}
                  {a.endedReason ? <span className="text-ink-2"> · {a.endedReason}</span> : null}
                </div>
              ))}
            </>
          ) : null}
          {view.data.otherReviews?.length ? (
            <>
              <h3 className="font-serif font-semibold text-table mt-6 mb-2">Reviews</h3>
              {view.data.otherReviews.map((r) => (
                <div key={r.id} className="border-b border-line py-2 text-small">
                  <span className="font-semibold">{r.reviewer}</span>{' '}
                  <span className="text-ink-2">({r.state.replace('_', ' ')})</span>
                  {r.scores ? (
                    <div className="mt-1 grid grid-cols-3 gap-x-4">
                      {(view.data.rubric?.criteria ?? []).map((c) => (
                        <div key={c.key} className={c.scale === 'text' ? 'col-span-3 whitespace-pre-wrap' : ''}>
                          <span className="text-ink-2">{c.label}:</span>{' '}
                          {showValue(r.scores?.[c.key]?.score ?? r.scores?.[c.key]?.rating ?? r.scores?.[c.key]?.text)}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </>
          ) : null}
          {view.data.decision ? (
            <>
              <h3 className="font-serif font-semibold text-table mt-6 mb-2">Decision</h3>
              <DecisionCell decision={view.data.decision} />
              {view.data.decision.rationale ? <p className="text-small mt-2">{view.data.decision.rationale}</p> : null}
            </>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

export function ApplicationsTab({ cycle }: { cycle: CycleDetail }) {
  const refresh = useCycleRefresh(cycle.id);
  const [open, setOpen] = useState<string | null>(null);
  const [resolving, setResolving] = useState<StaffApplication | null>(null);
  const [note, setNote] = useState('');
  const [show, setShow] = useState<'current' | 'replaced' | 'all'>('current');
  const [choosing, setChoosing] = useState<StaffApplication | null>(null);
  const [chooseReason, setChooseReason] = useState('');
  const apps = useQuery<StaffApplication[]>(
    ['review', cycle.id, 'applications'],
    async () => (await staffApi.get(path(cycle, '/applications'))).data.data,
  );
  const act = useMutation(async (fn: () => Promise<unknown>) => fn(), { onSuccess: refresh });
  const byId = new Map((apps.data ?? []).map((a) => [a.id, a]));
  const replacedCount = (apps.data ?? []).filter((a) => a.replacedById).length;
  const visible = (apps.data ?? []).filter((a) =>
    show === 'all' ? true : show === 'replaced' ? Boolean(a.replacedById) : !a.replacedById,
  );
  const choose = (keep: 'later' | 'earlier') =>
    act.mutate(async () => {
      await staffApi.post(path(cycle, `/applications/${choosing!.id}/choose-submission`), {
        keep,
        reason: chooseReason,
      });
      setChoosing(null);
      setChooseReason('');
    });
  const resolve = (status: string) =>
    act.mutate(async () => {
      await staffApi.post(path(cycle, `/applications/${resolving!.id}/eligibility`), { status, note });
      setResolving(null);
      setNote('');
    });

  return (
    <Panel>
      <PanelHeader title="Applications" note={apps.data ? `${apps.data.length - replacedCount}` : undefined}>
        {replacedCount ? (
          <select
            className={`${inputClass} w-auto`}
            value={show}
            onChange={(e) => setShow(e.target.value as typeof show)}
            aria-label="Which applications to show"
          >
            <option value="current">Current applications</option>
            <option value="replaced">Replaced ({replacedCount})</option>
            <option value="all">All submissions</option>
          </select>
        ) : null}
        {cycle.can['applications.refresh'] ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => act.mutate(() => staffApi.post(path(cycle, '/applications/refresh')))}
          >
            Refresh from the form
          </Button>
        ) : null}
        {cycle.can['eligibility.run'] ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => act.mutate(() => staffApi.post(path(cycle, '/eligibility/run')))}
          >
            Run eligibility
          </Button>
        ) : null}
      </PanelHeader>
      {act.error ? (
        <div className="p-5">
          <ErrorNotice>{errorMessage(act.error, 'That did not work.')}</ErrorNotice>
        </div>
      ) : null}
      {apps.isLoading ? (
        <SkeletonRows rows={6} />
      ) : !apps.data?.length ? (
        <EmptyState title="No applications yet">
          Applications are read from the cycle’s Fellow application form when intake closes, or with Refresh from the
          form.
        </EmptyState>
      ) : (
        <table className="w-full">
          <thead>
            <HeadRow>
              <Th>Applicant</Th>
              <Th>Career stage</Th>
              <Th>Discipline · region</Th>
              <Th>Eligibility</Th>
              <Th>Outcome</Th>
              <Th />
            </HeadRow>
          </thead>
          <tbody>
            {visible.map((a) => (
              <tr key={a.id} className={`border-b border-line ${a.withdrawn || a.replacedById ? 'opacity-50' : ''}`}>
                <Td>
                  <button type="button" className="link-underline" onClick={() => setOpen(a.id)}>
                    {a.name ?? '—'}
                  </button>
                  <div className="text-small text-ink-2">
                    {a.institution} {a.member ? '' : '· not a member'} {a.late ? '· late' : ''}{' '}
                    {a.withdrawn ? '· withdrawn' : ''}
                  </div>
                  {a.replacedById ? (
                    <div className="text-small mt-1">
                      <Pill tone="inactive">
                        {submittedBefore(byId.get(a.replacedById), a)
                          ? 'Replaced: earlier submission kept'
                          : 'Replaced by a later submission'}
                      </Pill>{' '}
                      <button type="button" className="link-underline" onClick={() => setOpen(a.replacedById)}>
                        see the one that stands
                      </button>
                    </div>
                  ) : null}
                  {a.waitingOnId ? (
                    <div className="text-small mt-1">
                      <Pill tone="failed">Later submission: choose which stands</Pill>
                    </div>
                  ) : null}
                </Td>
                <Td>{a.careerStage ?? '—'}</Td>
                <Td className="text-small">{[a.discipline, a.region].filter(Boolean).join(' · ')}</Td>
                <Td>
                  <StatusPill status={a.eligibilityStatus} />
                  {a.eligibilityFlags.map((f, i) => (
                    <div key={i} className="text-small text-ink-2 mt-1">
                      {f.message}
                    </div>
                  ))}
                  {a.eligibilityNote ? <div className="text-small mt-1">Staff: {a.eligibilityNote}</div> : null}
                </Td>
                <Td>{a.outcome ? OUTCOME_LABELS[a.outcome] : '—'}</Td>
                <Td>
                  {a.waitingOnId && cycle.can['application.choose_submission'] ? (
                    <Button size="sm" variant="primary" onClick={() => setChoosing(a)}>
                      Choose
                    </Button>
                  ) : null}
                  {a.replacedById || a.waitingOnId ? null : cycle.can['eligibility.resolve'] &&
                    (a.eligibilityStatus === 'flagged' || a.eligibilityResolved) ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setResolving(a);
                        setNote(a.eligibilityNote ?? '');
                      }}
                    >
                      Resolve
                    </Button>
                  ) : null}
                  {cycle.can['application.withdraw'] && !a.replacedById ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        const reason = await promptAction(
                          a.withdrawn
                            ? 'Restore this application? Reason:'
                            : 'Withdraw this application from the cycle? Reason:',
                        );
                        if (reason)
                          act.mutate(() => staffApi.post(path(cycle, `/applications/${a.id}/withdraw`), { reason }));
                      }}
                    >
                      {a.withdrawn ? 'Restore' : 'Withdraw'}
                    </Button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {resolving ? (
        <Modal
          title="Resolve eligibility"
          onClose={() => setResolving(null)}
          footer={
            <div className="flex justify-end gap-3 w-full">
              <Button variant="ghost" onClick={() => setResolving(null)}>
                Cancel
              </Button>
              <Button variant="destructive" disabled={!note.trim()} onClick={() => resolve('ineligible')}>
                Ineligible
              </Button>
              <Button variant="primary" onClick={() => resolve('eligible')}>
                Eligible
              </Button>
            </div>
          }
        >
          <p className="text-table mb-2 font-semibold">{resolving.name}</p>
          {resolving.eligibilityFlags.map((f, i) => (
            <p key={i} className="text-small text-ink-2">
              {f.message}
            </p>
          ))}
          <label className="block text-small text-ink-2 mt-3 mb-1">Note (required for ineligible; recorded)</label>
          <textarea className={`${inputClass} h-[72px]`} value={note} onChange={(e) => setNote(e.target.value)} />
          <p className="text-small text-ink-2 mt-2">
            Ineligible applicants get an Ineligible decision when the cycle moves to assignment.
          </p>
        </Modal>
      ) : null}
      {choosing ? (
        <Modal
          title="Which submission stands?"
          onClose={() => setChoosing(null)}
          width={760}
          footer={
            <div className="flex justify-end gap-3 w-full">
              <Button variant="ghost" onClick={() => setChoosing(null)}>
                Cancel
              </Button>
              <Button variant="outline" disabled={!chooseReason.trim()} onClick={() => choose('earlier')}>
                Keep the earlier submission
              </Button>
              <Button variant="primary" disabled={!chooseReason.trim()} onClick={() => choose('later')}>
                Keep the later submission
              </Button>
            </div>
          }
        >
          <p className="text-small text-ink-2 mb-3">
            Reviewers already have the earlier submission. Keeping the later one ends those assignments (their reviews
            are kept, never counted) and the later one goes through eligibility and assignment. The one not kept is
            marked replaced and is never sent to reviewers.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Earlier submission', app: byId.get(choosing.waitingOnId ?? '') },
              { label: 'Later submission', app: choosing },
            ].map(({ label, app }) => (
              <div key={label} className="border border-line p-3 text-small">
                <div className="font-semibold text-small text-ink-2 mb-2">{label}</div>
                {app ? (
                  <>
                    <button type="button" className="link-underline font-semibold" onClick={() => setOpen(app.id)}>
                      {app.name ?? '—'}
                    </button>
                    <div>Submitted {utcStamp(app.submittedAt)}</div>
                    <div>{app.email}</div>
                    <div>
                      {app.careerStage ?? '—'} · {app.institution ?? '—'}
                    </div>
                    <div>{[app.discipline, app.region].filter(Boolean).join(' · ')}</div>
                    <div className="mt-1">
                      <StatusPill status={app.eligibilityStatus} />
                    </div>
                  </>
                ) : (
                  '—'
                )}
              </div>
            ))}
          </div>
          <label className="block text-small text-ink-2 mt-3 mb-1">Reason (recorded)</label>
          <textarea
            className={`${inputClass} h-[60px]`}
            value={chooseReason}
            onChange={(e) => setChooseReason(e.target.value)}
          />
        </Modal>
      ) : null}
      {open ? <ApplicationModal cycle={cycle} applicationId={open} onClose={() => setOpen(null)} /> : null}
    </Panel>
  );
}

function submittedBefore(a: StaffApplication | undefined, b: StaffApplication): boolean {
  return Boolean(a?.submittedAt && b.submittedAt && a.submittedAt < b.submittedAt);
}

export function ConflictsTab({ cycle }: { cycle: CycleDetail }) {
  const refresh = useCycleRefresh(cycle.id);
  const flags = useQuery<Conflict[]>(
    ['review', cycle.id, 'conflicts'],
    async () => (await staffApi.get(path(cycle, '/conflicts'))).data.data,
  );
  const act = useMutation(async (fn: () => Promise<unknown>) => fn(), { onSuccess: refresh });
  return (
    <Panel>
      <PanelHeader title="Conflicts of interest" note={flags.data ? `${flags.data.length}` : undefined}>
        <Button
          size="sm"
          variant="outline"
          onClick={() => act.mutate(() => staffApi.post(path(cycle, '/conflicts/detect')))}
        >
          Detect now
        </Button>
      </PanelHeader>
      <PanelBody>
        <p className="text-small text-ink-2 mb-3">
          Hard conflicts (supervisor, trainee, family or personal, financial) block assignment and access. Soft
          conflicts (same institution, co-authored in the last 3 years) are flagged to the chair, who allows them or
          treats them as hard. Supervisor matches compare the application’s supervisor email with each member’s emails.
        </p>
        {act.error ? <ErrorNotice>{errorMessage(act.error, 'That did not work.')}</ErrorNotice> : null}
      </PanelBody>
      {!flags.data?.length ? (
        <EmptyState title="No conflicts recorded">
          Detection runs when assignments are proposed, or with Detect now.
        </EmptyState>
      ) : (
        <table className="w-full">
          <thead>
            <HeadRow>
              <Th>Member</Th>
              <Th>Applicant</Th>
              <Th>Conflict</Th>
              <Th>Source</Th>
              <Th>Status</Th>
              <Th />
            </HeadRow>
          </thead>
          <tbody>
            {flags.data.map((f) => (
              <tr key={f.id} className="border-b border-line">
                <Td>{f.member}</Td>
                <Td>{f.applicant}</Td>
                <Td>
                  <Pill tone={f.effective === 'hard' ? 'failed' : f.effective === 'soft' ? 'running' : 'inactive'}>
                    {f.level}
                  </Pill>{' '}
                  {f.typeLabel}
                  {f.detail ? <div className="text-small text-ink-2">{f.detail}</div> : null}
                </Td>
                <Td>{f.source}</Td>
                <Td>
                  {stateLabel(f.status)}
                  {f.decisionReason ? <div className="text-small text-ink-2">{f.decisionReason}</div> : null}
                </Td>
                <Td>
                  {f.canDismiss ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        const reason = await promptAction('Dismiss this automatic flag? Reason (recorded):');
                        if (reason)
                          act.mutate(() => staffApi.post(path(cycle, `/conflicts/${f.id}/dismiss`), { reason }));
                      }}
                    >
                      Dismiss
                    </Button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

export function AssignmentTab({ cycle }: { cycle: CycleDetail }) {
  const refresh = useCycleRefresh(cycle.id);
  const progress = useQuery<Progress>(
    ['review', cycle.id, 'progress'],
    async () => (await staffApi.get(path(cycle, '/progress'))).data.data,
  );
  const [result, setResult] = useState<string | null>(null);
  const [reassigning, setReassigning] = useState<{
    assignmentId: string;
    applicationId: string;
    who: string | null;
  } | null>(null);
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const act = useMutation(async (fn: () => Promise<string | null>) => fn(), {
    onSuccess: (msg) => {
      setResult(msg);
      refresh();
    },
  });
  const reviewers = progress.data?.reviewers ?? [];
  const proposedCount = reviewers.reduce((s, r) => s + r.proposed, 0);

  return (
    <div className="flex flex-col gap-6">
      <Panel>
        <PanelHeader title="Assignment">
          {cycle.can['assignment.propose'] ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                act.mutate(async () => {
                  const d = (await staffApi.post(path(cycle, '/assignments/propose'))).data.data;
                  return `Proposed ${d.proposed} assignments${d.unfilled.length ? `; ${d.unfilled.length} application(s) could not be filled` : ''}.`;
                })
              }
            >
              Propose
            </Button>
          ) : null}
          {cycle.can['assignment.approve'] && proposedCount ? (
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                act.mutate(async () => {
                  const d = (await staffApi.post(path(cycle, '/assignments/approve'), {})).data.data;
                  return `Approved ${d.approved}${d.blocked ? `; ${d.blocked} dropped for a hard conflict` : ''}.`;
                })
              }
            >
              Approve {proposedCount}
            </Button>
          ) : null}
          {cycle.can['notify.send'] ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                act.mutate(async () => {
                  const d = (await staffApi.post(path(cycle, '/notify'))).data.data;
                  return d.configured
                    ? `Notified ${d.sent} of ${d.reviewers} reviewers.`
                    : 'Email is not configured (SENDGRID_API_KEY); nothing was sent.';
                })
              }
            >
              Email reviewers
            </Button>
          ) : null}
        </PanelHeader>
        <PanelBody>
          <p className="text-small text-ink-2">
            {progress.data?.perApplication ?? 2} reviewers per eligible application, never one with a hard conflict,
            loads within one of each other, and each reviewer’s set spread across career stages, disciplines and
            regions. Self-identification is not an input. Emails say only how many applications a reviewer has.
          </p>
          {result ? (
            <div className="mt-3">
              <InfoNotice>{result}</InfoNotice>
            </div>
          ) : null}
          {act.error ? (
            <div className="mt-3">
              <ErrorNotice>{errorMessage(act.error, 'That did not work.')}</ErrorNotice>
            </div>
          ) : null}
        </PanelBody>
        <table className="w-full">
          <thead>
            <HeadRow>
              <Th>Reviewer</Th>
              <Th>Proposed</Th>
              <Th>Assigned</Th>
              <Th>Attested</Th>
              <Th>Submitted</Th>
              <Th>Conflicts declared</Th>
              <Th>Emailed</Th>
            </HeadRow>
          </thead>
          <tbody>
            {reviewers.map((r) => (
              <tr key={r.userId} className="border-b border-line tabular">
                <Td className="font-sans">{r.name}</Td>
                <Td>{r.proposed}</Td>
                <Td>{r.assigned}</Td>
                <Td>{r.attested}</Td>
                <Td>{r.submitted}</Td>
                <Td>{r.conflicted}</Td>
                <Td>{r.notified}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel>
        <PanelHeader title="By application" />
        {progress.isLoading ? (
          <SkeletonRows rows={6} />
        ) : (
          <table className="w-full">
            <thead>
              <HeadRow>
                <Th>Applicant</Th>
                <Th>Career stage · discipline · region</Th>
                <Th>Reviewers</Th>
              </HeadRow>
            </thead>
            <tbody>
              {(progress.data?.applications ?? [])
                .filter((a) => a.eligibilityStatus === 'eligible' && !a.withdrawn)
                .map((a) => (
                  <tr key={a.id} className={`border-b border-line ${a.needsReviewers ? 'bg-warning-tint' : ''}`}>
                    <Td>
                      {a.applicantName}
                      {a.needsReviewers ? (
                        <div className="text-small text-warning">needs {a.needsReviewers} more</div>
                      ) : null}
                    </Td>
                    <Td className="text-small">
                      {[a.careerStage, a.discipline, a.region].filter(Boolean).join(' · ')}
                    </Td>
                    <Td>
                      {a.assignments.map((s) => (
                        <div key={s.id} className="flex items-center gap-2 text-small py-[2px]">
                          <span>{s.reviewer}</span>
                          <StatusPill status={s.state === 'active' ? (s.review ?? 'not_started') : s.state} />
                          {s.purpose === 'third' ? <Pill tone="running">third</Pill> : null}
                          {s.softConflict ? <Pill tone="planned">soft conflict</Pill> : null}
                          {cycle.can['assignment.reassign'] &&
                          ['active', 'proposed', 'conflicted'].includes(s.state) ? (
                            <button
                              type="button"
                              className="link-underline text-small"
                              onClick={() => {
                                setReassigning({ assignmentId: s.id, applicationId: a.id, who: s.reviewer });
                                setTo('');
                                setReason('');
                              }}
                            >
                              Reassign
                            </button>
                          ) : null}
                        </div>
                      ))}
                    </Td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </Panel>
      {reassigning ? (
        <Modal
          title="Reassign"
          onClose={() => setReassigning(null)}
          footer={
            <div className="flex justify-end gap-3 w-full">
              <Button variant="ghost" onClick={() => setReassigning(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!to || !reason.trim()}
                onClick={() =>
                  act.mutate(async () => {
                    await staffApi.post(path(cycle, `/assignments/${reassigning.assignmentId}/reassign`), {
                      reviewerUserId: to,
                      reason,
                    });
                    setReassigning(null);
                    return 'Reassigned.';
                  })
                }
              >
                Reassign
              </Button>
            </div>
          }
        >
          <p className="text-table mb-3">
            Replace {reassigning.who}. Any review they started is withdrawn and kept for the record.
          </p>
          <label className="block text-small text-ink-2 mb-1">New reviewer</label>
          <select className={`${inputClass} mb-3`} value={to} onChange={(e) => setTo(e.target.value)}>
            <option value="">Choose…</option>
            {reviewers.map((r) => (
              <option key={r.userId} value={r.userId}>
                {r.name} ({r.assigned} assigned)
              </option>
            ))}
          </select>
          <label className="block text-small text-ink-2 mb-1">Reason (recorded)</label>
          <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Modal>
      ) : null}
    </div>
  );
}

export function CalibrationTab({ cycle }: { cycle: CycleDetail }) {
  const refresh = useCycleRefresh(cycle.id);
  const [open, setOpen] = useState<string | null>(null);
  const [third, setThird] = useState<string | null>(null);
  const [to, setTo] = useState('');
  const data = useQuery<Calibration>(
    ['review', cycle.id, 'calibration'],
    async () => (await staffApi.get(path(cycle, '/calibration'))).data.data,
  );
  const add = useMutation(
    async () =>
      staffApi.post(path(cycle, '/assignments'), { applicationId: third, reviewerUserId: to, purpose: 'third' }),
    {
      onSuccess: () => {
        setThird(null);
        refresh();
      },
    },
  );
  const reviewers = cycle.committee.filter((m) => m.seat === 'reviewer');
  return (
    <Panel>
      <PanelHeader title="Calibration" />
      <PanelBody>
        {data.isLoading ? (
          <LoadingPanel label="Loading…" height={160} />
        ) : data.error || !data.data ? (
          <ErrorNotice>{errorMessage(data.error, 'Could not load calibration.')}</ErrorNotice>
        ) : (
          <CalibrationTable
            data={data.data}
            onOpen={setOpen}
            onAddThird={
              data.data.canAddThird
                ? (id) => {
                    setThird(id);
                    setTo('');
                  }
                : undefined
            }
          />
        )}
      </PanelBody>
      {third ? (
        <Modal
          title="Add a third reviewer"
          onClose={() => setThird(null)}
          footer={
            <div className="flex justify-end gap-3 w-full">
              <Button variant="ghost" onClick={() => setThird(null)}>
                Cancel
              </Button>
              <Button variant="primary" disabled={!to || add.isLoading} onClick={() => add.mutate()}>
                Assign
              </Button>
            </div>
          }
        >
          <select className={inputClass} value={to} onChange={(e) => setTo(e.target.value)}>
            <option value="">Choose…</option>
            {reviewers.map((r) => (
              <option key={r.userId ?? r.id} value={r.userId ?? ''}>
                {r.name}
              </option>
            ))}
          </select>
          {add.error ? (
            <div className="mt-3">
              <ErrorNotice>{errorMessage(add.error, 'Could not assign.')}</ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}
      {open ? <ApplicationModal cycle={cycle} applicationId={open} onClose={() => setOpen(null)} /> : null}
    </Panel>
  );
}

export function DeliberationTab({ cycle }: { cycle: CycleDetail }) {
  const refresh = useCycleRefresh(cycle.id);
  const [open, setOpen] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const data = useQuery<Deliberation>(
    ['review', cycle.id, 'deliberation'],
    async () => (await staffApi.get(path(cycle, '/deliberation'))).data.data,
  );
  return (
    <div className="flex flex-col gap-6">
      <Panel>
        <PanelHeader title="Ranked list">
          {cycle.can.export ? (
            <Button size="sm" variant="outline" onClick={() => setExporting(true)}>
              Export
            </Button>
          ) : null}
        </PanelHeader>
        <PanelBody>
          {data.isLoading ? (
            <LoadingPanel label="Loading…" height={160} />
          ) : data.error || !data.data ? (
            <ErrorNotice>{errorMessage(data.error, 'Could not load deliberation.')}</ErrorNotice>
          ) : (
            <DeliberationTable
              data={data.data}
              onOpen={setOpen}
              onDecide={async (row, body) => {
                await staffApi.post(path(cycle, `/applications/${row.id}/decision`), body);
                refresh();
              }}
              onCondition={
                cycle.can['decision.condition']
                  ? async (decisionId, status) => {
                      await staffApi.patch(path(cycle, `/decisions/${decisionId}/condition`), { status });
                      refresh();
                    }
                  : undefined
              }
            />
          )}
        </PanelBody>
      </Panel>
      {data.data?.equity ? (
        <Panel>
          <PanelHeader title="Self-identification summary" note="inside the review module only" />
          <PanelBody>
            <EquitySummary equity={data.data.equity} />
          </PanelBody>
        </Panel>
      ) : null}
      {open ? <ApplicationModal cycle={cycle} applicationId={open} onClose={() => setOpen(null)} /> : null}
      {exporting ? (
        <ExportModal
          allowSelfId
          onClose={() => setExporting(false)}
          onExport={async (body) => {
            const res = await staffApi.post(path(cycle, '/export'), body, { responseType: 'blob' });
            downloadCsv(res.data, `fellow-selection-${cycle.year}.csv`, res.headers['content-disposition']);
          }}
        />
      ) : null}
    </div>
  );
}

export function AuditTab({ cycle }: { cycle: CycleDetail }) {
  const rows = useQuery<AuditRow[]>(
    ['review', cycle.id, 'audit'],
    async () => (await staffApi.get(path(cycle, '/audit'), { params: { limit: 500 } })).data.data,
  );
  return (
    <Panel>
      <PanelHeader title="Audit trail" note="append-only" />
      {rows.isLoading ? (
        <SkeletonRows rows={8} />
      ) : (
        <table className="w-full">
          <thead>
            <HeadRow>
              <Th>When (UTC)</Th>
              <Th>Who</Th>
              <Th>What</Th>
              <Th>Applicant</Th>
              <Th>State</Th>
              <Th>Reason · detail</Th>
            </HeadRow>
          </thead>
          <tbody>
            {(rows.data ?? []).map((r) => (
              <tr key={r.id} className="border-b border-line text-small">
                <Td className="tabular whitespace-nowrap text-small">{utcStamp(r.at)}</Td>
                <Td className="text-small">{r.actor}</Td>
                <Td className="text-small">{r.action.replace(/_/g, ' ')}</Td>
                <Td className="text-small">{r.applicant ?? ''}</Td>
                <Td className="text-small">{stateLabel(r.phase)}</Td>
                <Td className="text-small">
                  {r.reason ? <div>{r.reason}</div> : null}
                  {r.detail ? (
                    <code className="text-small text-ink-2 break-all">{JSON.stringify(r.detail)}</code>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
